import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageState from '../components/common/PageState';
import ResultLoadingState from '../components/common/ResultLoadingState';
import {useAuth} from '../hooks/useAuth';
import {useAppDispatch, useAppSelector} from '../hooks/redux';
import {fetchTests, selectSubscription, selectTests, selectTestsByName} from '../slices/testSlice';
import {
    restoreCurrentPatientFromStorage,
    selectCurrentPatient,
    selectSubPatients,
    setCurrentPatient,
} from '../slices/patientSlice';
import {generateDummyTests} from '../utils/dummyData';
import SingleResultContent from './SingleResultContent';
import type {Test} from '../types/api';

const PatientSingleResult: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const {user, token} = useAuth();
    const dispatch = useAppDispatch();

    const currentPatient = useAppSelector(selectCurrentPatient);
    const subPatients = useAppSelector(selectSubPatients);
    const testsState = useAppSelector(selectTests);
    const currentPatientData = useAppSelector((state) => selectTestsByName(state, currentPatient?.fullName || ''));
    const subscription = useAppSelector(selectSubscription);
    const isMember = subscription;

    // Track if we've attempted to fetch data to prevent loops with empty responses
    const [hasFetchedData, setHasFetchedData] = useState(false);

    // Reset fetch flag when user changes (e.g., during impersonation)
    useEffect(() => {
        setHasFetchedData(false);
    }, [user?.userInfo?.id]);

    // Initialize data when component mounts and user is authenticated
    useEffect(() => {
        const shouldFetchTests =
            user?.isLoggedIn &&
            !testsState.loading &&
            !hasFetchedData && // Prevent infinite loops with empty responses
            (testsState.tests.length === 0 || // No tests loaded yet
                (subPatients.length === 0 && testsState.tests.length > 0) || // Have tests but no patients (possible after token refresh)
                ((!currentPatient?.id || currentPatient.id === 0) && subPatients.length === 0)); // No valid current patient and no sub patients

        if (shouldFetchTests) {
            setHasFetchedData(true);
            dispatch(fetchTests());
        }
    }, [
        dispatch,
        user?.isLoggedIn,
        testsState.tests.length,
        testsState.loading,
        subPatients.length,
        currentPatient?.id,
        hasFetchedData,
    ]);

    // Restore current patient from localStorage on page load (handles token refresh scenarios)
    useEffect(() => {
        if ((!currentPatient?.id || currentPatient.id === 0) && user?.token) {
            dispatch(restoreCurrentPatientFromStorage());
        }
    }, [dispatch, currentPatient?.id, user?.token]);

    // Set current patient from subPatients data (extracted from test results)
    useEffect(() => {
        if (currentPatient?.id && currentPatient.id > 0) {
            return;
        }
        if (subPatients.length === 0) {
            return;
        }

        // Current patient (if any) is restored in the previous effect; if it's still missing,
        // default to the first valid patient extracted from the test results.
        const fallbackPatient = subPatients.find((p) => p.id > 0 && p.firstName && p.lastName);
        if (fallbackPatient) {
            dispatch(setCurrentPatient(fallbackPatient));
        }
    }, [dispatch, currentPatient?.id, subPatients]);

    // Generate dummy data for display when not subscribed
    const dummyData = React.useMemo(() => {
        if (subscription) return null; // Don't generate if subscribed

        const currentPatientName =
            currentPatient?.firstName && currentPatient?.lastName
                ? {firstName: currentPatient.firstName, lastName: currentPatient.lastName}
                : {firstName: 'Sample', lastName: 'Patient'};

        return generateDummyTests(currentPatientName);
    }, [subscription, currentPatient?.firstName, currentPatient?.lastName]);

    // Find the specific test result
    let result: Test | null = null;
    let realResult: Test | null = null; // Keep real result for PDF download

    // Always find real result for PDF downloads
    for (let i = 0; i < currentPatientData.length; i++) {
        if (currentPatientData[i].id.toString() === id) {
            realResult = currentPatientData[i];
            break;
        }
    }

    if (subscription) {
        // If subscribed, use real data
        result = realResult;
    } else {
        // If not subscribed, use dummy data but find a matching dummy test
        if (dummyData && dummyData.length > 0) {
            // Use the first dummy test, but keep the real ID for PDF downloads
            result = {
                ...dummyData[0],
                id: realResult?.id || parseInt(id || '0'), // Keep real ID for PDF
                elis_id: realResult?.elis_id || parseInt(id || '0'), // Keep real elis_id for PDF
            };
        } else {
            result = realResult; // Fallback to real data if dummy generation fails
        }
    }

    if (testsState.loading) {
        return (
            <DashboardLayout isMember={isMember}>
                <ResultLoadingState />
            </DashboardLayout>
        );
    }

    if (!result) {
        return (
            <DashboardLayout isMember={isMember}>
                <PageState
                    title="Result Not Found"
                    description="The requested test result could not be found."
                    primaryAction={{label: 'Back', onClick: () => navigate('/results')}}
                />
            </DashboardLayout>
        );
    }

    return (
        <SingleResultContent
            result={result}
            realResult={realResult}
            isMember={isMember}
            token={token}
            backTo="/results"
            backLabel="Back to Results"
        />
    );
};

export default PatientSingleResult;
